/**
 * Lane CQ-261 (census X5 — "one period vocabulary; every drill carries the period": five sets of period words, and the Topics page's
 * "7 days" covered 8) — the console's ONE period vocabulary.  Every page that names a period says it through `periodWords` (the
 * `period.*` key family in en.json — never a page's own "Last N days"), and computes its UTC days through the ONE rule,
 * `periodBounds` (pages/daily/overviewModel.js, re-exported here): "Last N days" = today and the N−1 days before it, both ends
 * included; "This month" = the 1st to today; "This year" = 1 January to today; a custom range = the two days picked, both included.
 *
 *   periodWords(t, 'today' | '7d' | '30d' | '90d' | 'month' | 'year' | 'custom')   "Last 7 days", "This month", "Custom range"
 *   periodWords(t, 14)  /  lastDaysWords(t, 14)                                     "Last 14 days" (any whole number of days)
 *
 * A page whose server reads a rolling window of whole days (Diagnostics, Unanswered) names its window in the same words.  Pure.
 * The census (utils/period.census.cq261.test.js) holds every console page to it; the pages still owed to their lanes are an exact list
 * there (a file a lane converts leaves the list with it).
 */
// The day rule itself — `periodBounds` / `periodOf` / `windowDaysOf` — lives in pages/daily/overviewModel.js (imported from there, not
// re-exported here: overviewModel reads these words, and a re-export would make the two modules import each other).

/** The named choices of the vocabulary, in its order. */
export const PERIOD_CHOICE_IDS = Object.freeze(['today', '7d', '30d', '90d', 'month', 'year', 'custom']);

const WINDOW_OF = Object.freeze({ '7d': 7, '30d': 30, '90d': 90 });
/** The windows the console offers have whole-phrase keys (each translated as one phrase, as the retired families were); any other
 *  number of days reads the counted phrase. */
const NAMED_WINDOWS = Object.freeze([7, 14, 30, 90]);

/** "Last 7 days" for a number of whole days (one day reads "Today"). */
export function lastDaysWords(t, days) {
  const n = Math.max(1, Math.round(Number(days) || 0));
  if (n === 1) return t('period.choice.today');
  if (NAMED_WINDOWS.includes(n)) return t(`period.choice.${n}d`);
  return t('period.lastDays', { count: n });
}

/** A period in words: a named choice of the vocabulary, or a number of days. */
export function periodWords(t, id) {
  if (typeof id === 'number' || /^\d+$/.test(String(id ?? ''))) return lastDaysWords(t, Number(id));
  if (WINDOW_OF[id]) return lastDaysWords(t, WINDOW_OF[id]);
  if (PERIOD_CHOICE_IDS.includes(id)) return t(`period.choice.${id}`);
  return t('period.choice.custom');
}
